/* eslint-disable no-unused-vars */
import React, { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import Teachr from "../../Objects/models/Teachr";
import DialogLayout from "../../Components/DialogLayout";
import SessionDescriptionRepository from "../../Repositories/SessionDescriptionRepository";
import { SHOW_ERROR, SHOW_SUCCESS } from "../../Redux/Reducers/MessageReducers";
import { fetchExternalOverlap } from "../DeclarationCourse/sessionConflicts";
import { useTravelScenario } from "../DeclarationCourse/Travel/TravelModeSelector";
import { DV2 } from "../DeclarationV2/theme";
import { clock, dayLabel, plural } from "../DeclarationV2/format";
import { vocab } from "../DeclarationV2/vocabulary";
import { useDv2Theme } from "../DeclarationV2/layout/Ui";
import { Draft } from "./types";
import { draftIssues, draftPeriod, hasDraftIssues } from "./drafts";
import submitDraft, { DeclarationError } from "./api/submitDraft";

/** Résultat d'une vérification refusée : la page de modification affiche ces erreurs. */
export interface DraftRefusal {
    /** Chevauchement avec un autre cours du prof : message et séance concernée (index 1..n). */
    overlap?: { message: string; index: number } | null;
}

interface Options {
    /** Vérification refusée : la liste redirige vers la page de modification, la page affiche les erreurs. */
    onRefused: (draft: Draft, refusal: DraftRefusal) => void;
    /** Déclaration acceptée : retour à la liste, rechargement. */
    onDeclared: (draft: Draft, courseId: number) => void;
}

/**
 * Déclarer un brouillon (liste et page de modification) : contrôles locaux
 * (`draftIssues`), chevauchement avec un autre cours du prof, scénario de
 * déplacement, confirmation, envoi (`submitDraft`). Même chaîne que le menu
 * « Déclarer » de l'onglet existant, sans ses popups : un refus renvoie vers
 * la page de modification.
 */
const useDeclareDraft = (teachr: Teachr, options: Options) => {
    const dispatch = useDispatch();
    const { fontFamily } = useDv2Theme();
    const { checkTravelScenario, travelModals, travelLoading } = useTravelScenario(teachr);
    const [checking, setChecking] = useState(false);
    const [sending, setSending] = useState(false);
    const [pending, setPending] = useState<Draft | null>(null);

    const declare = useCallback(async (draft: Draft) => {
        const issues = draftIssues(draft, teachr);
        if (hasDraftIssues(issues)) {
            options.onRefused(draft, {});
            return;
        }
        setChecking(true);
        try {
            // chevauchement avec un autre cours du prof : le cours source est exclu, il va être repris
            const overlap = await fetchExternalOverlap(teachr?.id, draft.sessions, draft.id);
            if (overlap) {
                options.onRefused(draft, {
                    overlap: {
                        index: overlap.index,
                        message: `Chevauche ${vocab.theCourse()} déjà déclaré de ${overlap.existingSlot} avec ${overlap.studentName}. Décalez le début ou la date de cette séance.`,
                    },
                });
                return;
            }
        } finally {
            setChecking(false);
        }
        const { start, end } = draftPeriod(draft);
        const child = {
            clientId: draft.student.clientId,
            addressId: draft.student.addressId,
            childFirstName: draft.student.firstName,
            childLastName: draft.student.lastName,
        };
        checkTravelScenario(child, () => setPending(draft), { start: start?.toDate(), end: end?.toDate() });
    }, [teachr, options, checkTravelScenario]);

    const confirm = useCallback(async () => {
        if (!pending) return;
        setSending(true);
        try {
            let generic: string | null = null;
            try {
                const response = await SessionDescriptionRepository.get(teachr.id as number);
                generic = response?.sessionDescription?.description ?? null;
            } catch {
                generic = null;
            }
            const { courseId } = await submitDraft(pending, teachr, generic);
            dispatch({ type: SHOW_SUCCESS, payload: `Votre ${vocab.course()} a bien été ${vocab.declared()}. Il apparaît déjà dans votre tableau de bord !` });
            const declared = pending;
            setPending(null);
            options.onDeclared(declared, courseId);
        } catch (e: any) {
            const message = e instanceof DeclarationError ? e.message : `La déclaration ${vocab.ofTheCourse()} a échoué. Vérifiez votre saisie puis réessayez.`;
            dispatch({ type: SHOW_ERROR, payload: message, title: "Attention !" });
            setPending(null);
        } finally {
            setSending(false);
        }
    }, [pending, teachr, dispatch, options]);

    const period = pending ? draftPeriod(pending) : null;
    const when = period?.start && period?.end
        ? pending!.multiple
            ? `${plural(pending!.sessions.length, "séance")} entre le ${dayLabel(period.start).toLowerCase()} et le ${dayLabel(period.end).toLowerCase()}`
            : `le ${dayLabel(period.start).toLowerCase()} de ${clock(period.start)} à ${clock(period.end)}`
        : "";

    const dialogs = (
        <>
            {travelModals}
            <DialogLayout
                title={`Déclaration ${vocab.aCourse().replace(/^un /, "d'un ").replace(/^une /, "d'une ")} ✏️`}
                text={pending ? (
                    <p style={{ fontFamily, fontSize: 15, lineHeight: 1.55, color: DV2.text, margin: 0 }}>
                        Vous êtes sur le point de déclarer {vocab.theCourse()} de <strong>{pending.student.firstName} {pending.student.lastName}</strong>, {when}.
                        Si vous souhaitez confirmer, cliquez sur <strong>Déclarer</strong>.
                    </p>
                ) : null}
                valid="Déclarer"
                cancel="Annuler"
                open={!!pending}
                load={sending}
                handleSubmit={confirm}
                handleClose={() => { if (!sending) setPending(null); }}
            />
        </>
    );

    return { declare, dialogs, busy: checking || sending || travelLoading };
};

export default useDeclareDraft;
